import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaUserCircle } from 'react-icons/fa';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isApiConfigured, listCategories, peekCached } from '../lib/api';
import { useProfile } from '../lib/useProfile';
import { Category } from '../types/content';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  // Seeded from the last fetch so the section links are present in the first
  // paint. Starting empty made the whole nav bar reflow a moment after load.
  const [categories, setCategories] = useState<Category[]>(
    () => peekCached<Category[]>('categories', 5 * 60 * 1000) ?? []
  );
  const { profile } = useProfile();
  const navigate = useNavigate();

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    checkUser();

    // Listen for auth events (sign in, sign out)
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
      }
    });

    return () => hubListener();
  }, []);

  // Sections double as the site's top-level navigation.
  useEffect(() => {
    if (!isApiConfigured) return;
    let cancelled = false;
    listCategories()
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch(() => {
        /* Nav degrades to Home-only; not worth surfacing to readers. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <NavLink to="/" className="navbar-logo" onClick={() => setIsOpen(false)}>
          QED AI
        </NavLink>
        
        <div className="menu-icon" onClick={toggleMenu}>
          {isOpen ? <span>{React.createElement(FaTimes as any)}</span> : <span>{React.createElement(FaBars as any)}</span>}
        </div>

        <ul className={isOpen ? 'nav-menu active' : 'nav-menu'}>
          <li className="nav-item">
            <NavLink to="/" className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")} onClick={() => setIsOpen(false)}>
              Home
            </NavLink>
          </li>
          {categories.map((category) => (
            <li className="nav-item" key={category.id}>
              <NavLink
                to={`/category/${category.slug}`}
                className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")}
                onClick={() => setIsOpen(false)}
              >
                {category.name}
              </NavLink>
            </li>
          ))}

          {/* Admin tools are only advertised to actual admins. The API enforces
              this independently — this just stops readers seeing a dead link. */}
          {profile?.isAdmin && (
            <li className="nav-item">
                <NavLink to="/admin" className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")} onClick={() => setIsOpen(false)}>
                Admin
                </NavLink>
            </li>
          )}

          {/* Raising feedback is only half of it — without somewhere to track
              what happened, a report is posted into a void. */}
          {user && (
            <li className="nav-item">
                <NavLink to={profile?.isAdmin ? '/admin/feedback' : '/my-feedback'} className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")} onClick={() => setIsOpen(false)}>
                Feedback
                </NavLink>
            </li>
          )}

          {user && (
            <li className="nav-item">
                <NavLink to="/my-posts" className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")} onClick={() => setIsOpen(false)}>
                Write
                </NavLink>
            </li>
          )}

          {user && (
            <li className="nav-item">
                <NavLink to="/profile" className={({ isActive }) => "nav-links" + (isActive ? " active-link" : "")} onClick={() => setIsOpen(false)}>
                Profile
                </NavLink>
            </li>
          )}
          
          {user ? (
             <li className="nav-item">
                <div className="nav-links" onClick={() => { handleSignOut(); setIsOpen(false); }} style={{cursor: 'pointer'}}>
                   Sign Out
                </div>
              </li>
          ) : (
             <li className="nav-item">
                <NavLink to="/login" className="nav-links" onClick={() => setIsOpen(false)}>
                   Sign In
                </NavLink>
             </li>
          )}
          
          <li className="nav-item">
              <NavLink to={user ? "/profile" : "/login"} className="nav-links-mobile" onClick={() => setIsOpen(false)}>
                  <span>{React.createElement(FaUserCircle as any, { size: 24 })}</span>
              </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;

